import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { NewspaperIcon, PlayIcon, BookOpenIcon, SearchIcon, FilterIcon, CalendarIcon, UsersIcon, TagIcon, ArrowRightIcon, ChevronRightIcon, DownloadIcon, ExternalLinkIcon, CheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const MediaInsightsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const topics = ['All Topics', 'Microbiome', 'Gut Health', 'Probiotics', 'Nutrition', 'Supplements', 'Research Methods', 'Clinical Trials', 'Industry Standards'];
  // Featured content
  const featuredContent = [{
    id: 1,
    title: 'The Future of Microbiome Research: 2025 Outlook',
    type: 'Article',
    date: 'January 15, 2024',
    author: 'Dr. Maria Chen',
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    excerpt: 'An in-depth analysis of emerging trends and breakthrough technologies in microbiome science that will shape the next decade of research and therapeutics.',
    topics: ['Microbiome', 'Research Methods'],
    link: '#article-future'
  }, {
    id: 2,
    title: 'Ukweli Talks: Probiotics Beyond the Hype',
    type: 'Video',
    date: 'December 8, 2023',
    author: 'Dr. James Wilson & Dr. Lisa Park',
    image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    excerpt: 'Our most-watched webinar featuring leading experts discussing what the science really says about probiotics and their impact on health outcomes.',
    topics: ['Probiotics', 'Gut Health'],
    duration: '48:22',
    link: '#video-probiotics'
  }, {
    id: 3,
    title: 'Evidence-Based Guide to Evaluating Supplement Claims',
    type: 'Guide',
    date: 'November 20, 2023',
    author: 'Ukweli Research Team',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
    excerpt: 'A comprehensive framework for consumers and practitioners to critically assess health claims on supplement products.',
    topics: ['Supplements', 'Industry Standards'],
    link: '#guide-claims'
  }];
  // All content items
  const allContent = [...featuredContent, {
    id: 4,
    title: 'The Truth About Probiotics: Separating Science from Marketing',
    type: 'Article',
    date: 'August 15, 2023',
    author: 'Dr. Sarah Chen',
    image: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'An evidence-based analysis of common probiotic claims and what the research actually supports.',
    topics: ['Probiotics', 'Supplements'],
    link: '/article/probiotics-truth'
  }, {
    id: 5,
    title: 'Microbiome Research Explained: Interview with Dr. Sarah Chen',
    type: 'Video',
    date: 'July 28, 2023',
    author: 'Ukweli Media Team',
    image: 'https://images.unsplash.com/photo-1551817958-d9d86fb29431?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Leading researcher Dr. Sarah Chen breaks down complex microbiome concepts into accessible insights.',
    topics: ['Microbiome', 'Research Methods'],
    duration: '36:45',
    link: '/video/microbiome-explained'
  }, {
    id: 6,
    title: 'How Diet Affects Your Microbiome: New Research Findings',
    type: 'Article',
    date: 'June 12, 2023',
    author: 'Dr. Michael Rodriguez',
    image: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Recent studies reveal surprising connections between specific dietary patterns and microbiome diversity.',
    topics: ['Nutrition', 'Microbiome'],
    link: '/article/diet-microbiome'
  }, {
    id: 7,
    title: "Ukweli Institute's Guide to Microbiome Testing",
    type: 'Guide',
    date: 'May 30, 2023',
    author: 'Ukweli Research Team',
    image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
    excerpt: 'A comprehensive overview of current microbiome testing methods, their reliability, and how to interpret results.',
    topics: ['Microbiome', 'Research Methods'],
    link: '/guide/microbiome-testing'
  }, {
    id: 8,
    title: 'The Microbiome-Gut-Brain Axis: Emerging Science',
    type: 'Podcast',
    date: 'April 18, 2023',
    author: 'Dr. Emily Johnson & Dr. David Kim',
    image: 'https://images.unsplash.com/photo-1589903308904-1010c2294adc?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Exploring the fascinating bidirectional communication between gut microbes and the brain.',
    topics: ['Microbiome', 'Gut Health'],
    duration: '52:18',
    link: '/podcast/gut-brain-axis'
  }, {
    id: 9,
    title: 'Certification Standards in the Supplement Industry',
    type: 'Whitepaper',
    date: 'March 5, 2023',
    author: 'Ukweli Regulatory Team',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'An analysis of current regulatory frameworks and the need for stronger evidence-based standards.',
    topics: ['Supplements', 'Industry Standards'],
    link: '/whitepaper/certification-standards'
  }, {
    id: 10,
    title: 'Probiotic Strains: A Clinical Comparison',
    type: 'Research Summary',
    date: 'February 12, 2023',
    author: 'Dr. Robert Chang',
    image: 'https://images.unsplash.com/photo-1581093458791-9a9cd7093ae3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Comparing the clinical evidence behind the most common probiotic strains in commercial products.',
    topics: ['Probiotics', 'Clinical Trials'],
    link: '/research/probiotic-strains'
  }, {
    id: 11,
    title: 'Ukweli Insights: The Science of Fermented Foods',
    type: 'Video',
    date: 'January 25, 2023',
    author: 'Dr. Lisa Park',
    image: 'https://images.unsplash.com/photo-1563355846-0e8ad5491037?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'Exploring the microbiological processes and health benefits of traditional fermented foods.',
    topics: ['Nutrition', 'Microbiome'],
    duration: '28:42',
    link: '/video/fermented-foods'
  }, {
    id: 12,
    title: 'Consumer Guide: Reading Supplement Labels',
    type: 'Guide',
    date: 'December 10, 2022',
    author: 'Ukweli Education Team',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    excerpt: 'A practical guide to understanding supplement facts panels and identifying quality products.',
    topics: ['Supplements', 'Industry Standards'],
    link: '/guide/supplement-labels'
  }];
  const getTypeIcon = type => {
    switch (type) {
      case 'Article':
        return <NewspaperIcon size={18} className="text-blue-600" data-id="element-893" data-mp="1t04ob3ca19kx" />;
      case 'Video':
        return <PlayIcon size={18} className="text-blue-600" data-id="element-894" data-mp="1o08j50rufc3s" />;
      case 'Guide':
        return <BookOpenIcon size={18} className="text-blue-600" data-id="element-895" data-mp="1j9s276otd8co" />;
      case 'Podcast':
        return <div size={18} className="text-blue-600" data-id="element-896" data-mp="1eayope9j1hci" />;
      case 'Whitepaper':
        return <BookOpenIcon size={18} className="text-blue-600" data-id="element-897" data-mp="1e8ulkyjgcmda" />;
      case 'Research Summary':
        return <NewspaperIcon size={18} className="text-blue-600" data-id="element-898" data-mp="199rgxf7dwi5u" />;
      default:
        return <NewspaperIcon size={18} className="text-blue-600" data-id="element-899" data-mp="158phoydgw63v" />;
    }
  };
  // Filter content based on active tab, search query, and selected topic
  const filteredContent = allContent.filter(item => {
    const matchesTab = activeTab === 'all' || item.type.toLowerCase() === activeTab.toLowerCase();
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTopic = selectedTopic === 'All Topics' || item.topics.includes(selectedTopic);
    return matchesTab && matchesSearch && matchesTopic;
  });
  return <div className="bg-white w-full" data-id="element-900" data-mp="36zg7tfm4et2i">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-901" data-mp="3707zifclk6ni">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-902" data-mp="3bysrjrhp3k0v">
          <div className="text-center" data-id="element-903" data-mp="2easfj02yf3fg">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-904" data-mp="2ebu1hzlidaby">
              Media & Insights
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-905" data-mp="2jby7uwspcoog">
              Evidence-based articles, videos, and resources to help you
              navigate the complex world of microbiome science
            </p>
          </div>
        </div>
      </section>
      {/* Featured Content */}
      <section className="py-16 bg-white" data-id="element-906" data-mp="3ktwcs2js2of">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-907" data-mp="8hatgbrq5rdn">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="mb-16" data-id="element-908" data-mp="8jnammpnrxpn">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-909" data-mp="5lxcdw6mbmoa">
              Featured Content
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-910" data-mp="am30519rop13">
              {featuredContent.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col h-full" data-id="element-911" data-mp="3e0fezidsia0i" data-mpi={index}>
                  <div className="relative h-48" data-id="element-912" data-mp="2ityc04h7wqp9">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-913" data-mp="2e0dba4o8pimd" />
                    {item.type === 'Video' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-914" data-mp="251rvgha5khpa">
                        <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-915" data-mp="1zzsrb56l9gra">
                          <PlayIcon size={24} data-id="element-916" data-mp="1v3c5wiqrxghw" />
                        </div>
                      </div>}
                    <div className="absolute top-4 left-4" data-id="element-917" data-mp="1q50rdupjahtc">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-918" data-mp="3pgk3uja1q87r">
                        {getTypeIcon(item.type)}
                        <span className="ml-1" data-id="element-919" data-mp="1irjftjoaa6jt">{item.type}</span>
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-grow flex flex-col" data-id="element-920" data-mp="1nkcnogf1tv04">
                    <div className="flex items-center text-sm text-gray-500 mb-3" data-id="element-921" data-mp="18un65amnfedo">
                      <CalendarIcon size={14} className="mr-1" data-id="element-922" data-mp="1dsf86xh0cgvl" />
                      <span data-id="element-923" data-mp="1dqvthbb02dvx">{item.date}</span>
                      {item.duration && <>
                          <span className="mx-2" data-id="element-924" data-mp="26hm37mh96oc3">•</span>
                          <span data-id="element-925" data-mp="26h41wbfqhjxb">{item.duration}</span>
                        </>}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight" data-id="element-926" data-mp="1sk7ao8vpe0fw">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-4 flex-grow" data-id="element-927" data-mp="h1mpds2owaq4">
                      {item.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4" data-id="element-928" data-mp="l5ue3p95dqoc">
                      {item.topics.map(topic => <span key={topic} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-929" data-mp="dmuvvmxfa56k">
                          <TagIcon size={12} className="mr-1" data-id="element-930" data-mp="ijbn4pzx4znv" />
                          {topic}
                        </span>)}
                    </div>
                    <div className="mt-auto" data-id="element-931" data-mp="3qxbyjtzabyz1">
                      <Link to={item.link} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-932" data-mp="3m1mrl5kxrfsp">
                        Read more{' '}
                        <ChevronRightIcon size={16} className="ml-1" data-id="element-933" data-mp="3vwohi3n1clwc" />
                      </Link>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Content Library */}
      <section className="py-16 bg-gray-50" data-id="element-934" data-mp="26kjphak6thgo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-935" data-mp="2ajsjmgj53hcr">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-936" data-mp="2ai7lyi1j3iao">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-937" data-mp="2u7i5y88b4pel">
              Content Library
            </h2>
            {/* Search and Filter */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-id="element-938" data-mp="2y81y0xe0x2tv">
              <div className="flex flex-col md:flex-row gap-4" data-id="element-939" data-mp="338g1e24ojolk">
                <div className="flex-grow" data-id="element-940" data-mp="13wnbepgb7jic">
                  <div className="relative" data-id="element-941" data-mp="13xye0ly0ql7v">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-942" data-mp="18uf43los39nl">
                      <SearchIcon size={18} className="text-gray-400" data-id="element-943" data-mp="11bp8cwyw3d6r" />
                    </div>
                    <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by title or keyword" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-944" data-mp="wkfko1wwijxi" />
                  </div>
                </div>
                <div className="w-full md:w-64" data-id="element-945" data-mp="1ab2ilruu85sj">
                  <div className="relative" data-id="element-946" data-mp="1adyhbieieyi1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-947" data-mp="169h3dzutzff6">
                      <FilterIcon size={18} className="text-gray-400" data-id="element-948" data-mp="3v34dgchqnrvn" />
                    </div>
                    <select className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)} data-id="element-949" data-mp="3qzybvi5kmovi">
                      {topics.map(topic => <option key={topic} value={topic} data-id="element-950" data-mp="3nhit4q1w6j31">
                          {topic}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
              {/* Content Type Tabs */}
              <div className="mt-6 border-b border-gray-200" data-id="element-951" data-mp="2zual40d2wvxb">
                <nav className="-mb-px flex space-x-8" data-id="element-952" data-mp="34sc46lai8v8s">
                  <button onClick={() => setActiveTab('all')} className={`${activeTab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm`} data-id="element-953" data-mp="34su720roa7po">
                    All
                  </button>
                  <button onClick={() => setActiveTab('article')} className={`${activeTab === 'article' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-954" data-mp="277szkgi8wqpa">
                    <NewspaperIcon size={16} className="mr-2" data-id="element-955" data-mp="2q5n4aicccg1v" />
                    Articles
                  </button>
                  <button onClick={() => setActiveTab('video')} className={`${activeTab === 'video' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-956" data-mp="33xjs4a6z83sg">
                    <PlayIcon size={16} className="mr-2" data-id="element-957" data-mp="38u0hf7pwwo74" />
                    Videos
                  </button>
                  <button onClick={() => setActiveTab('guide')} className={`${activeTab === 'guide' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-958" data-mp="k5iw5u0ccon0">
                    <BookOpenIcon size={16} className="mr-2" data-id="element-959" data-mp="g8l4tbjirv8" />
                    Guides
                  </button>
                  <button onClick={() => setActiveTab('podcast')} className={`${activeTab === 'podcast' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-960" data-mp="5ethghno8ree">
                    <div size={16} className="mr-2" data-id="element-961" data-mp="3isn35jiomwbd" />
                    Podcasts
                  </button>
                </nav>
              </div>
            </div>
            {/* Content List */}
            {filteredContent.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-962" data-mp="36bccnawb98dh">
                {filteredContent.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index % 2 * 0.1
            }} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col md:flex-row" data-id="element-963" data-mp="2wcermhy3fwz8" data-mpi={index}>
                    <div className="md:w-2/5 h-48 md:h-auto relative" data-id="element-964" data-mp="thsckh8v2jxy">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" onError={e => {
                  e.target.src = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80';
                }} data-id="element-965" data-mp="tgzd81c8ewpu" />
                      {item.type === 'Video' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-966" data-mp="5qvea79znl56">
                          <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-967" data-mp="tmvq5kxhf3e">
                            <PlayIcon size={24} data-id="element-968" data-mp="3sy798fhj6j3h" />
                          </div>
                        </div>}
                      {item.type === 'Podcast' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-969" data-mp="3izrem2jaorp8">
                          <div className="bg-blue-600/80 rounded-full p-3 text-white" data-id="element-970" data-mp="1yfm8yr6vr1hc">
                            <BookOpenIcon size={24} data-id="element-971" data-mp="23f8v5q4ors2a" />
                          </div>
                        </div>}
                    </div>
                    <div className="p-6 md:w-3/5 flex flex-col justify-between" data-id="element-972" data-mp="28d0q6t0mnuap">
                      <div data-id="element-973" data-mp="2cfnkr1oontbl">
                        <div className="flex items-center mb-2" data-id="element-974" data-mp="1fq698630rd1z">
                          {getTypeIcon(item.type)}
                          <span className="ml-1 text-sm font-semibold text-blue-600" data-id="element-975" data-mp="1kor7hzypnmns">
                            {item.type}
                          </span>
                          <span className="mx-2 text-gray-400" data-id="element-976" data-mp="1oorljjhwdvub">•</span>
                          <span className="text-sm text-gray-500" data-id="element-977" data-mp="1tie05h7mg9sr">
                            {item.date}
                          </span>
                          {item.duration && <>
                              <span className="mx-2 text-gray-400" data-id="element-978" data-mp="10zgw2y5zznvh">•</span>
                              <span className="text-sm text-gray-500" data-id="element-979" data-mp="24awamdww548n">
                                {item.duration}
                              </span>
                            </>}
                        </div>
                        <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-980" data-mp="2e3tkxwrqbmw6">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-3" data-id="element-981" data-mp="1leeb3absw01u">
                          {item.excerpt}
                        </p>
                        {item.author && <p className="text-sm text-gray-700 mb-3" data-id="element-982" data-mp="1uej7yqjdbg1r">
                            By:{' '}
                            {item.author === 'Dr. Sarah Chen' ? <Link to="/profile" className="text-blue-600 hover:text-blue-800" data-id="element-983" data-mp="35t6hx7w1pii7">
                                {item.author}
                              </Link> : item.author}
                          </p>}
                        <div className="flex flex-wrap gap-2 mb-2" data-id="element-984" data-mp="2zdpvrpxdrpl0">
                          {item.topics.map(topic => <span key={topic} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-985" data-mp="34cj8hg4ufck4">
                              {topic}
                            </span>)}
                        </div>
                      </div>
                      <Link to={item.link} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium mt-2 text-sm" data-id="element-986" data-mp="3n9aq7l4qljm2">
                        Read more <ArrowRightIcon size={16} className="ml-1" data-id="element-987" data-mp="jnv4p7l5od95" />
                      </Link>
                    </div>
                  </motion.div>)}
              </div> : <div className="text-center py-12 bg-white rounded-lg shadow-md" data-id="element-988" data-mp="2n64i8hyfrcnl">
                <SearchIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-989" data-mp="2i4x7qb4wrzd8" />
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-990" data-mp="2d6vpfsfb442t">
                  No results found
                </h3>
                <p className="text-gray-600" data-id="element-991" data-mp="3avf8v6m2nqo0">
                  Try adjusting your search or filters
                </p>
              </div>}
            {/* Pagination - would be implemented with actual pagination logic */}
            {filteredContent.length > 0 && <div className="mt-12 flex justify-center" data-id="element-992" data-mp="2w46e6j3bbf40">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-993" data-mp="3tk3tdrgn7tz9">
                  <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-994" data-mp="wdhud82dxc6">
                    <span className="sr-only" data-id="element-995" data-mp="9w0houl5av34">Previous</span>
                    <ChevronRightIcon size={16} className="transform rotate-180" data-id="element-996" data-mp="ew4zcnxshvux" />
                  </a>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-997" data-mp="juzkvxn55yiq">
                    1
                  </a>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-blue-50 text-sm font-medium text-blue-600 hover:bg-blue-100" data-id="element-998" data-mp="xlbczwjq7x9v">
                    2
                  </a>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-999" data-mp="hw55cfej48i1">
                    3
                  </a>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700" data-id="element-1000" data-mp="mvrkwvhty0u0">
                    ...
                  </span>
                  <a href="#" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1001" data-mp="10ozayiq9io9l">
                    8
                  </a>
                  <a href="#" className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1002" data-mp="15n0ut5viirm4">
                    <span className="sr-only" data-id="element-1003" data-mp="1mvtrcgyrl6bu">Next</span>
                    <ChevronRightIcon size={16} data-id="element-1004" data-mp="1mwd6jpx15gm6" />
                  </a>
                </nav>
              </div>}
          </motion.div>
        </div>
      </section>
      {/* Resources Section */}
      <section className="py-16 bg-white" data-id="element-1005" data-mp="1d3nzr8cfcqx6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1006" data-mp="1i4blbehvwfk0">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-1007" data-mp="1i2ih05uqu7f6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1008" data-mp="z57k2rwl3q2w">
              Educational Resources
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1009" data-mp="2olqf20sz4o1u">
              <div className="bg-blue-50 rounded-lg p-8 shadow-md" data-id="element-1010" data-mp="2tiyz66xon25q">
                <div className="flex items-center mb-4" data-id="element-1011" data-mp="1gcxe6hzq3hk7">
                  <DownloadIcon size={24} className="text-blue-600 mr-3" data-id="element-1012" data-mp="1gcduowufcl42" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1013" data-mp="1ccmxaaae76qk">
                    Downloadable Resources
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-1014" data-mp="12ls9v2leu13j">
                  Access our collection of free educational materials including
                  infographics, fact sheets, and research summaries.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-1015" data-mp="sn32w4v1y122">
                  <li className="flex items-start" data-id="element-1016" data-mp="sm9qoodbqjcj">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1017" data-mp="olhi7xca0ihb" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1018" data-mp="jor2xc8apqwy">
                      Probiotic Strains Comparison Chart
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1019" data-mp="2e28st5w6azv5">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1020" data-mp="2lkq6lob310tx" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1021" data-mp="2qi8gbgwxbk2z">
                      Supplement Evaluation Checklist
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1022" data-mp="309md3gxhq5sf">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1023" data-mp="30b5td7j5cgu7" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1024" data-mp="27jvyl6ry51vs">
                      Microbiome Testing Guide
                    </a>
                  </li>
                </ul>
                <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1025" data-mp="2gllyz23t3b5p">
                  View all resources{' '}
                  <ArrowRightIcon size={16} className="ml-1" data-id="element-1026" data-mp="3rzhdc8mvzfvv" />
                </a>
              </div>
              <div className="bg-blue-50 rounded-lg p-8 shadow-md" data-id="element-1027" data-mp="mxz3j005njsg">
                <div className="flex items-center mb-4" data-id="element-1028" data-mp="rvr8aulwf0e2">
                  <UsersIcon size={24} className="text-blue-600 mr-3" data-id="element-1029" data-mp="rv92p7dcj9th" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1030" data-mp="d636ztdpw1qc">
                    Webinars & Events
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-1031" data-mp="i3uxqk2mlhsy">
                  Join our live webinars and virtual events featuring expert
                  discussions on the latest microbiome research.
                </p>
                <div className="space-y-4 mb-6" data-id="element-1032" data-mp="3qdo5vvh1wcmg">
                  <div className="bg-white p-4 rounded-md" data-id="element-1033" data-mp="3qey18cwir6pe">
                    <p className="text-sm text-blue-600 font-semibold" data-id="element-1034" data-mp="3vc7pvqc33ec6">
                      UPCOMING
                    </p>
                    <h4 className="font-bold mb-1" data-id="element-1035" data-mp="1oncl00e2u1k9">
                      Microbiome & Immune Health
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1036" data-mp="1tkl5i7mp6hoo">
                      May 15, 2024 • 2:00 PM EST
                    </p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1037" data-mp="394lt3oik3548">
                      Register now
                    </a>
                  </div>
                  <div className="bg-white p-4 rounded-md" data-id="element-1038" data-mp="3j1gohyjj8pea">
                    <p className="text-sm text-blue-600 font-semibold" data-id="element-1039" data-mp="o8u9runx38m">
                      UPCOMING
                    </p>
                    <h4 className="font-bold mb-1" data-id="element-1040" data-mp="3st3221jcf0k7">
                      Supplement Industry Standards Panel
                    </h4>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1041" data-mp="alx0pf1mrz6m">
                      June 8, 2024 • 1:00 PM EST
                    </p>
                    <a href="#" className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-1042" data-mp="2cdtcmprpnb1x">
                      Register now
                    </a>
                  </div>
                </div>
                <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1043" data-mp="bdep5bmphkv3">
                  View all events <ArrowRightIcon size={16} className="ml-1" data-id="element-1044" data-mp="kbqnjzttdmy3" />
                </a>
              </div>
              <div className="bg-blue-50 rounded-lg p-8 shadow-md" data-id="element-1045" data-mp="pcedei6b3xqq">
                <div className="flex items-center mb-4" data-id="element-1046" data-mp="ub7qwallfoqw">
                  <ExternalLinkIcon size={24} className="text-blue-600 mr-3" data-id="element-1047" data-mp="u8llagi9jjbb" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1048" data-mp="z87xqnq9ovj2">
                    For Professionals
                  </h3>
                </div>
                <p className="text-gray-700 mb-6" data-id="element-1049" data-mp="181ur67705mp3">
                  Specialized resources for healthcare providers, researchers,
                  and industry professionals.
                </p>
                <ul className="space-y-3 mb-6" data-id="element-1050" data-mp="1i0a9plryrnmz">
                  <li className="flex items-start" data-id="element-1051" data-mp="1i1208i6pev6e">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1052" data-mp="1s3xnvslcd5fi" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1053" data-mp="1n4ck94v888wg">
                      Clinical Application Guidelines
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1054" data-mp="1e57mi0rhojlc">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1055" data-mp="1e6sjry56ugmw" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1056" data-mp="25wn9vx0veacm">
                      Research Collaboration Opportunities
                    </a>
                  </li>
                  <li className="flex items-start" data-id="element-1057" data-mp="1x3tttxdln6xp">
                    <BookOpenIcon size={16} className="text-blue-600 mr-2 mt-1" data-id="element-1058" data-mp="1x31xulg3b76w" />
                    <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-1059" data-mp="2oty7ld6usvpy">
                      Certification Program Details
                    </a>
                  </li>
                </ul>
                <a href="#" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1060" data-mp="2ec2u3etqpejj">
                  Access professional resources{' '}
                  <ArrowRightIcon size={16} className="ml-1" data-id="element-1061" data-mp="24eevgdt0jmw3" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Newsletter Signup */}
      <section className="py-16 bg-gray-50" data-id="element-1062" data-mp="2x0zorjt1u92a">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1063" data-mp="2x3vmp84wcxqq">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-1064" data-mp="2j9us8qkz9ww6">
            <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1065" data-mp="14bqo3bjp1050">
              Stay Informed
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-1066" data-mp="zcdzal99hizs">
              Subscribe to our newsletter for the latest research insights,
              educational resources, and event announcements
            </p>
            <div className="max-w-lg mx-auto" data-id="element-1067" data-mp="lefofpu5g4p1">
              <form className="sm:flex" data-id="element-1068" data-mp="lf7kt2vkm6gd">
                <label htmlFor="email-address" className="sr-only" data-id="element-1069" data-mp="gfma5v71g9nt">
                  Email address
                </label>
                <input id="email-address" name="email" type="email" autoComplete="email" required className="w-full px-5 py-3 placeholder-gray-500 focus:ring-white focus:border-white sm:max-w-xs border-white rounded-md" placeholder="Enter your email" data-id="element-1070" data-mp="vdeq2oghz02j" />
                <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3 sm:flex-shrink-0" data-id="element-1071" data-mp="28wnhfsx9cv1u">
                  <button type="submit" className="w-full flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-600 focus:ring-white" data-id="element-1072" data-mp="2duyqi1fqb1j0">
                    Subscribe
                  </button>
                </div>
              </form>
              <p className="mt-3 text-sm text-blue-100" data-id="element-1073" data-mp="1v50jlono3egy">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
            <div className="mt-10 flex justify-center space-x-6" data-id="element-1074" data-mp="24usgipj8ojol">
              <a href="#" className="text-white hover:text-blue-100" data-id="element-1075" data-mp="24wd8c7e65oir">
                <span className="sr-only" data-id="element-1076" data-mp="17ewf7f2nnaow">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1077" data-mp="1cdpxrlwstrvx">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" data-id="element-1078" data-mp="3lme4zb3go0nc" />
                </svg>
              </a>
              <a href="#" className="text-white hover:text-blue-100" data-id="element-1079" data-mp="2xx3c0fscrajr">
                <span className="sr-only" data-id="element-1080" data-mp="2xv8r31h2zifo">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1081" data-mp="2szbacu1woa2d">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" data-id="element-1082" data-mp="2izsscj5k2ycy" />
                </svg>
              </a>
              <a href="#" className="text-white hover:text-blue-100" data-id="element-1083" data-mp="6p9dyi39t5tx">
                <span className="sr-only" data-id="element-1084" data-mp="3o6ajeoe8a5se">YouTube</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1085" data-mp="3o5haphvpl67m">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-1086" data-mp="3f6njy8527a1z" />
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};